import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
export function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const faqs = [{
    category: 'Getting Started',
    questions: [{
      q: 'How do I create an account on Citiplots?',
      a: 'Click "Sign Up" in the top right corner. You can register using email, phone, or social login (Google, LinkedIn, Facebook). Verify your email/phone with OTP to activate your account.'
    }, {
      q: 'Is Citiplots free to use?',
      a: 'Browsing listings and contacting sellers is completely free. We charge a small platform fee (2%) only when a transaction is successfully completed.'
    }, {
      q: 'What types of properties are listed on Citiplots?',
      a: 'We list residential plots, commercial land, agricultural land, and government auction properties across India. All listings are verified for legal compliance.'
    }]
  }, {
    category: 'Buying Process',
    questions: [{
      q: 'How do I know if a listing is genuine?',
      a: 'Every listing goes through our verification process. Look for the "Verified" badge which means we\'ve checked ownership documents, legal clearances, and seller identity.'
    }, {
      q: 'Can I negotiate the price?',
      a: 'Yes! Use the "Make Offer" button on any listing to submit your offer. The seller will respond within 48 hours. You can also message sellers directly to discuss pricing.'
    }, {
      q: 'What is earnest money and is it refundable?',
      a: "Earnest money (typically 10-20% of offer) is held in escrow to show serious intent. It's refundable if the seller rejects your offer or if due diligence reveals issues. It's non-refundable if you back out after acceptance."
    }, {
      q: 'How long does a typical transaction take?',
      a: 'From offer acceptance to final registration typically takes 30-60 days, depending on document verification, legal clearances, and payment processing.'
    }]
  }, {
    category: 'Selling Process',
    questions: [{
      q: 'How do I list my property?',
      a: 'Click "Post Listing" and fill in property details, upload documents (7/12 extract, title deed, etc.), and set your price. Our team will verify documents within 48 hours before publishing.'
    }, {
      q: 'What documents do I need to list my property?',
      a: 'Required: 7/12 extract, title deed, NA order (if applicable), property tax receipts, and owner ID proof. Optional but recommended: survey map, encumbrance certificate, and NOC from society.'
    }, {
      q: 'How much does it cost to list?',
      a: 'Listing is free! We only charge a 2% platform fee when your property is successfully sold.'
    }]
  }, {
    category: 'Legal & Documentation',
    questions: [{
      q: 'What is RERA and why is it important?',
      a: 'RERA (Real Estate Regulatory Authority) ensures transparency and accountability in real estate. All our developer projects are RERA-registered, protecting buyer interests.'
    }, {
      q: 'Do you provide legal assistance?',
      a: 'Yes! We have partnerships with legal firms for document verification, title search, and registration assistance. Contact our support team for details.'
    }, {
      q: "What happens if there's a dispute?",
      a: 'Our platform has a built-in dispute resolution process. We mediate between parties and, if needed, can escalate to legal arbitration. All transactions are logged and documented.'
    }]
  }, {
    category: 'Payments & Security',
    questions: [{
      q: 'How are payments processed?',
      a: 'We use Razorpay for secure payment processing. Earnest money is held in escrow until transaction completion. Final payments can be made via bank transfer, cheque, or online.'
    }, {
      q: 'Is my financial information secure?',
      a: 'Yes. We use bank-grade encryption (SSL/TLS) and never store your complete payment details. All transactions comply with RBI guidelines.'
    }, {
      q: 'What if I need to cancel a transaction?',
      a: 'Cancellation terms depend on the stage: Before offer acceptance - full refund. After acceptance but before document signing - earnest money may be forfeited. After signing - subject to agreement terms.'
    }]
  }];
  const filteredFAQs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(faq => faq.q.toLowerCase().includes(searchQuery.toLowerCase()) || faq.a.toLowerCase().includes(searchQuery.toLowerCase()))
  })).filter(category => category.questions.length > 0);
  return <div className="min-h-screen bg-[#f5f5f7] font-sans text-[#1f2933] flex flex-col" data-id="element-4080">
      <header className="bg-white border-b border-gray-300 py-4 px-6 md:px-12" data-id="element-4081">
        <Link to="/" className="flex items-center gap-2" data-id="element-4082">
          <div className="w-8 h-8 bg-[#1f2933] rounded-sm" data-id="element-4083"></div>
          <span className="font-bold text-xl tracking-tight" data-id="element-4084">
            Citiplots.com
          </span>
        </Link>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-6 py-12" data-id="element-4085">
        <h1 className="text-4xl font-bold mb-4" data-id="element-4086">Frequently Asked Questions</h1>
        <p className="text-lg text-gray-500 mb-8 font-mono" data-id="element-4087">
          Find answers to common questions about Citiplots
        </p>

        {/* Search */}
        <div className="mb-8" data-id="element-4088">
          <div className="relative" data-id="element-4089">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4090" />
            <input type="text" placeholder="Search FAQs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-300 bg-white focus:outline-none focus:border-gray-500" data-id="element-4091" />
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8" data-id="element-4092">
          {filteredFAQs.map((category, catIndex) => <div key={catIndex} className="bg-white border border-gray-300 p-6" data-id="element-4093">
              <h2 className="font-bold text-xl mb-4" data-id="element-4094">{category.category}</h2>
              <div className="space-y-3" data-id="element-4095">
                {category.questions.map((faq, qIndex) => {
              const globalIndex = catIndex * 100 + qIndex;
              const isOpen = openIndex === globalIndex;
              return <div key={qIndex} className="border border-gray-200" data-id="element-4096">
                      <button onClick={() => setOpenIndex(isOpen ? null : globalIndex)} className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50" data-id="element-4097">
                        <span className="font-bold text-sm" data-id="element-4098">{faq.q}</span>
                        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-500" data-id="element-4099" /> : <ChevronDown className="w-5 h-5 text-gray-500" data-id="element-4100" />}
                      </button>
                      {isOpen && <div className="p-4 pt-0 text-sm text-gray-600 leading-relaxed" data-id="element-4101">
                          {faq.a}
                        </div>}
                    </div>;
            })}
              </div>
            </div>)}
        </div>

        {/* Still Have Questions */}
        <div className="mt-12 bg-white border border-gray-300 p-8 text-center" data-id="element-4102">
          <h3 className="font-bold text-lg mb-2" data-id="element-4103">Still have questions?</h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4104">
            Our support team is here to help
          </p>
          <Link to="/contact" className="inline-block px-6 py-2 bg-[#1f2933] text-white font-bold hover:bg-gray-800" data-id="element-4105">
            Contact Support
          </Link>
        </div>
      </main>

      <Footer data-id="element-4106" />
    </div>;
}